import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../components/ui/dialog";
import {
  EnvelopeSimple, Clock, ArrowRight, Bell, CheckCircle, Warning, Users, Heartbeat,
  FileText, UserPlus, XCircle, MagnifyingGlass, CaretDown, CaretUp, ArrowsClockwise, Crosshair, Paperclip, Globe
} from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  under_review: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  approved: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  rejected: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
};

const STATUS_LABELS = {
  pending: "Pending", under_review: "Under Review", approved: "Approved", rejected: "Rejected",
};

export default function SupplierOnboardingPage() {
  const [tab, setTab] = useState("applications");

  return (
    <div data-testid="supplier-onboarding-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold font-[Chivo] tracking-tight">Supplier Onboarding</h1>
          <p className="text-xs text-muted-foreground mt-0.5">Manage applications, portal activations, and automated reminders</p>
        </div>
      </div>

      {/* Tab Switcher */}
      <div className="flex gap-1 border-b border-border mb-6" data-testid="onboarding-tabs">
        {[
          { id: "applications", label: "Applications", icon: FileText },
          { id: "reminders", label: "Reminders & Activations", icon: Bell },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium border-b-2 transition-colors -mb-px ${
              tab === t.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`tab-${t.id}`}
          >
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "applications" && <ApplicationsTab />}
      {tab === "reminders" && <RemindersTab />}
    </div>
  );
}

/* ────────── Applications Tab ────────── */
function ApplicationsTab() {
  const [apps, setApps] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [checkedIds, setCheckedIds] = useState([]);

  const fetchApps = useCallback(async () => {
    try {
      const params = filter !== "all" ? { status: filter } : {};
      const { data } = await API.get("/supplier-applications", { params });
      setApps(data.applications || []);
      setCounts(data.counts || {});
    } catch { toast.error("Failed to load applications"); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetchApps(); }, [fetchApps]);

  const handleStatusChange = async (appId, status, notes) => {
    setActionLoading(true);
    try {
      const { data } = await API.put(`/supplier-applications/${appId}/status`, { status, admin_notes: notes });
      toast.success(data?.message || `Application ${STATUS_LABELS[status].toLowerCase()}`);
      fetchApps();
      if (status === "rejected") setSelected(null);
      else setSelected(prev => (prev && prev.id === appId ? { ...prev, status, admin_notes: notes } : prev));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
    finally { setActionLoading(false); }
  };

  const handleOnboard = async (appId) => {
    setActionLoading(true);
    try {
      const { data } = await API.post(`/supplier-applications/${appId}/onboard`);
      toast.success(`${data.company_name} added to the platform${data.welcome_pack_sent ? " — portal invite emailed" : " (no email on file, invite not sent)"}${data.documents_carried ? `, ${data.documents_carried} document(s) carried over` : ""}`);
      setSelected(null);
      fetchApps();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to onboard"); }
    finally { setActionLoading(false); }
  };

  const handleMerge = async (appId) => {
    setActionLoading(true);
    try {
      const { data } = await API.post(`/supplier-applications/${appId}/merge`);
      toast.success(`Profile updated for ${data.company_name} — ${data.updated_fields} field${data.updated_fields === 1 ? "" : "s"} refreshed${data.documents_carried ? `, ${data.documents_carried} document(s) added` : ""}`);
      setSelected(null);
      fetchApps();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to merge"); }
    finally { setActionLoading(false); }
  };

  const handleConvert = async (appId) => {
    setActionLoading(true);
    try {
      const { data } = await API.post(`/supplier-applications/${appId}/convert`);
      toast.success(data.message);
      fetchApps();
      setSelected(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to convert"); }
    finally { setActionLoading(false); }
  };

  const toggleChecked = (appId) => {
    setCheckedIds(p => p.includes(appId) ? p.filter(i => i !== appId) : [...p, appId]);
  };

  const handleBulkConvert = async () => {
    if (!checkedIds.length) return;
    setActionLoading(true);
    try {
      const { data } = await API.post("/supplier-applications/bulk-convert", { app_ids: checkedIds });
      if (data.converted_count) {
        const packs = (data.converted || []).filter(c => c.welcome_pack_sent).length;
        toast.success(`${data.converted_count} application${data.converted_count === 1 ? "" : "s"} approved & converted${packs ? ` — ${packs} welcome pack${packs === 1 ? "" : "s"} emailed` : ""}`);
      }
      if (data.skipped_count) toast.warning(`${data.skipped_count} skipped: ${data.skipped.map(s => `${s.app_id} (${s.reason})`).join(", ")}`);
      setCheckedIds([]);
      fetchApps();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk convert failed"); }
    finally { setActionLoading(false); }
  };

  const filtered = apps.filter(a => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (a.first_name + " " + a.last_name).toLowerCase().includes(q) ||
      (a.company_name || "").toLowerCase().includes(q) ||
      (a.email || "").toLowerCase().includes(q) ||
      (a.id || "").toLowerCase().includes(q);
  });

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;

  return (
    <div>
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6" data-testid="application-stats">
        <CountCard label="Total" count={counts.total || 0} active={filter === "all"} onClick={() => setFilter("all")} />
        <CountCard label="Pending" count={counts.pending || 0} color="amber" active={filter === "pending"} onClick={() => setFilter("pending")} />
        <CountCard label="Under Review" count={counts.under_review || 0} color="blue" active={filter === "under_review"} onClick={() => setFilter("under_review")} />
        <CountCard label="Approved" count={counts.approved || 0} color="emerald" active={filter === "approved"} onClick={() => setFilter("approved")} />
        <CountCard label="Rejected" count={counts.rejected || 0} color="red" active={filter === "rejected"} onClick={() => setFilter("rejected")} />
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input placeholder="Search by name, company, email, or reference..." value={search} onChange={e => setSearch(e.target.value)}
          className="pl-8 text-xs" data-testid="application-search" />
      </div>

      {/* Bulk action bar */}
      {checkedIds.length > 0 && (
        <div className="flex items-center justify-between gap-3 mb-3 border border-emerald-200 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg px-4 py-2.5" data-testid="bulk-action-bar">
          <p className="text-xs font-medium text-emerald-700 dark:text-emerald-300">{checkedIds.length} application{checkedIds.length === 1 ? "" : "s"} selected</p>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setCheckedIds([])} data-testid="bulk-clear-btn">Clear</Button>
            <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={handleBulkConvert} disabled={actionLoading} data-testid="bulk-convert-btn">
              <UserPlus size={13} className="mr-1" /> Approve & Convert {checkedIds.length}
            </Button>
          </div>
        </div>
      )}

      {/* Application List */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-lg" data-testid="no-applications">
          <FileText size={28} className="mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">No applications found</p>
          <p className="text-[10px] text-muted-foreground mt-1">Share the application link: <code className="bg-muted px-1.5 py-0.5 rounded">/apply</code></p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="application-list">
          {filtered.map(app => (
            <div key={app.id}
              className="flex items-center justify-between p-4 border border-border rounded-lg bg-card hover:bg-muted/30 transition-colors cursor-pointer"
              onClick={() => setSelected(app)}
              data-testid={`application-row-${app.id}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                {!app.converted_vendor_id && (
                  <span onClick={(e) => e.stopPropagation()} className="shrink-0">
                    <Checkbox checked={checkedIds.includes(app.id)} onCheckedChange={() => toggleChecked(app.id)} data-testid={`app-check-${app.id}`} />
                  </span>
                )}
                <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <UserPlus size={18} className="text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold truncate">{app.first_name} {app.last_name}</p>
                  <p className="text-[10px] text-muted-foreground truncate">
                    {app.company_name || "Individual"} &middot; {app.core_trade || "No trade specified"} &middot; {app.email}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0 ml-3">
                {app.gap_matches > 0 && (
                  <Badge className="text-[10px] bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300 gap-1" data-testid={`gap-match-badge-${app.id}`}>
                    <Crosshair size={11} weight="bold" /> Fills {app.gap_matches} gap{app.gap_matches === 1 ? "" : "s"}
                  </Badge>
                )}
                <span className="text-[10px] text-muted-foreground hidden sm:inline">{app.id}</span>
                {app.source && app.source !== "portal" && (
                  <Badge className="text-[10px] bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200 gap-1" title={app.external_reference ? `Website reference ${app.external_reference}` : "Received from the website"} data-testid={`source-badge-${app.id}`}>
                    <Globe size={10} /> {app.source}{app.external_reference ? ` · ${app.external_reference}` : ""}
                  </Badge>
                )}
                {app.linked_vendor_id && (
                  <Badge className="text-[10px] bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300" data-testid={`profile-update-badge-${app.id}`}>Profile Update</Badge>
                )}
                <Badge className={`text-[10px] ${STATUS_COLORS[app.status]}`}>{STATUS_LABELS[app.status]}</Badge>
                <span className="text-[10px] text-muted-foreground">{new Date(app.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</span>
                <ArrowRight size={14} className="text-muted-foreground" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail Dialog */}
      {selected && (
        <ApplicationDetailDialog
          app={selected}
          onClose={() => setSelected(null)}
          onStatusChange={handleStatusChange}
          onConvert={handleConvert}
          onOnboard={handleOnboard}
          onMerge={handleMerge}
          loading={actionLoading}
        />
      )}
    </div>
  );
}

function CountCard({ label, count, color = "primary", active, onClick }) {
  const ring = active ? "ring-2 ring-primary ring-offset-2 ring-offset-background" : "";
  const colors = {
    primary: "text-foreground",
    amber: "text-amber-600 dark:text-amber-400",
    blue: "text-blue-600 dark:text-blue-400",
    emerald: "text-emerald-600 dark:text-emerald-400",
    red: "text-red-600 dark:text-red-400",
  };
  return (
    <button onClick={onClick}
      className={`border border-border rounded-lg p-3 bg-card text-left transition-all hover:bg-muted/30 ${ring}`}
      data-testid={`filter-${label.toLowerCase().replace(/\s/g, "-")}`}
    >
      <p className={`text-lg font-bold font-[Chivo] ${colors[color]}`}>{count}</p>
      <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</p>
    </button>
  );
}

/* ────────── Application Detail Dialog ────────── */
function ApplicationDetailDialog({ app, onClose, onStatusChange, onConvert, onOnboard, onMerge, loading }) {
  const navigate = useNavigate();
  const [notes, setNotes] = useState(app.admin_notes || "");
  const [expanded, setExpanded] = useState({});
  const [checklist, setChecklist] = useState(null);
  const [mergePreview, setMergePreview] = useState(null);

  useEffect(() => {
    API.get(`/supplier-applications/${app.id}/checklist`).then(({ data }) => setChecklist(data)).catch(() => {});
    if (app.linked_vendor_id && !app.converted_vendor_id) {
      API.get(`/supplier-applications/${app.id}/merge-preview`).then(({ data }) => setMergePreview(data)).catch(() => {});
    }
  }, [app.id, app.linked_vendor_id, app.converted_vendor_id]);

  const toggle = (key) => setExpanded(p => ({ ...p, [key]: !p[key] }));

  const viewDoc = async (d) => {
    if (!d.file_id) { toast.error("No file attached"); return; }
    try {
      const res = await API.get(`/files/${d.file_id}/${encodeURIComponent(d.file_name || "document")}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const w = window.open(url, "_blank");
      if (!w) { const a = document.createElement("a"); a.href = url; a.download = d.file_name || "document"; a.click(); }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Could not open document");
    }
  };

  const sections = [
    { key: "origin", title: "Source", items: [
      ["Source", app.source && app.source !== "portal" ? app.source : "Portal application form"],
      ["External reference", app.external_reference],
      ["Received", app.created_at ? new Date(app.created_at).toLocaleString("en-GB") : ""],
    ]},
    { key: "personal", title: "Personal Details", items: [
      ["Name", `${app.first_name} ${app.last_name}`],
      ["Position", app.position],
      ["Email", app.email],
      ["Phone", app.contact_number],
      ["Address", [app.street_address, app.address_line_2, app.city, app.county, app.post_code, app.country].filter(Boolean).join(", ")],
    ]},
    { key: "company", title: "Company Details", items: [
      ["Company Name", app.company_name],
      ["Registration No.", app.company_registration_number],
      ["VAT Number", app.vat_number],
      ["Company Email", app.company_email],
      ["Company Phone", app.company_phone],
      ["Website", app.company_website],
      ["Uses Subcontractors", app.uses_subcontractors],
      ["% of Work Outsourced", app.subcontractor_percentage],
      ["Subcontractors Fully Vetted", app.subcontractors_vetted],
      ["Emergency Contact", (app.emergency_contact_methods || []).join(", ")],
    ]},
    { key: "skills", title: "Skills & Qualifications", items: [
      ["Core Trade", app.core_trade],
      ["Specialist Skills", app.specialist_skills],
      ["Trade Qualifications", app.trade_qualifications],
      ["Certifications", app.specialist_certifications],
      ["Areas Covered", app.areas_covered],
    ]},
    { key: "financial", title: "Charges & Financial", items: [
      ["Call Out Charges", app.call_out_charges],
      ["Payment Terms", app.payment_terms],
      ["Bank Account Name", app.bank_account_name],
      ["Sort Code", app.bank_sort_code],
      ["Account Number", app.bank_account_number],
    ]},
    { key: "legal", title: "Legal Documents Held", items:
      (app.legal_documents_held || []).length > 0
        ? app.legal_documents_held.map(d => [d, ""])
        : [["None declared", ""]]
    },
  ];

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="application-detail-dialog" aria-describedby="app-detail-desc">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 font-[Chivo]">
            <span>Application: {app.id}</span>
            {app.source && app.source !== "portal" && (
              <Badge className="text-[10px] bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200 gap-1" data-testid="detail-source-badge"><Globe size={10} /> {app.source}</Badge>
            )}
            {app.linked_vendor_id && (
              <Badge className="text-[10px] bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300" data-testid="detail-profile-update-badge">Profile Update</Badge>
            )}
            <Badge className={`text-[10px] ${STATUS_COLORS[app.status]}`}>{STATUS_LABELS[app.status]}</Badge>
          </DialogTitle>
        </DialogHeader>

        {app.gap_matches > 0 && (
          <div className="border border-violet-200 dark:border-violet-900 bg-violet-50 dark:bg-violet-900/20 rounded-md p-3" data-testid="gap-match-panel">
            <p className="text-xs font-semibold text-violet-700 dark:text-violet-300 flex items-center gap-1.5 mb-1">
              <Crosshair size={13} weight="bold" /> Fills {app.gap_matches} coverage gap{app.gap_matches === 1 ? "" : "s"}
            </p>
            <p className="text-[11px] text-violet-600 dark:text-violet-400">
              {(app.gap_match_samples || []).join(", ")}{app.gap_matches > (app.gap_match_samples || []).length ? ` +${app.gap_matches - app.gap_match_samples.length} more` : ""}
            </p>
          </div>
        )}
        <p id="app-detail-desc" className="sr-only">Review supplier application details and take action</p>

        <div className="space-y-3 mt-2">
          {sections.map(s => {
            const filteredItems = s.items.filter(([, v]) => v);
            if (filteredItems.length === 0) return null;
            const isOpen = expanded[s.key] !== false; // default open
            return (
              <div key={s.key} className="border border-border rounded-lg overflow-hidden">
                <button onClick={() => toggle(s.key)}
                  className="w-full flex items-center justify-between px-4 py-2.5 bg-muted/30 hover:bg-muted/50 transition-colors"
                  data-testid={`section-toggle-${s.key}`}
                >
                  <span className="text-xs font-semibold">{s.title}</span>
                  {isOpen ? <CaretUp size={14} /> : <CaretDown size={14} />}
                </button>
                {isOpen && (
                  <div className="px-4 py-3 grid gap-1.5">
                    {filteredItems.map(([label, value], i) => (
                      <div key={i} className="flex items-start gap-2 text-xs">
                        {label && value !== "" && <span className="text-muted-foreground w-36 shrink-0">{label}:</span>}
                        <span className="break-all">{value || label}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {checklist && (
          <div className="border border-border rounded-lg overflow-hidden" data-testid="onboard-checklist">
            <div className="px-4 py-2.5 bg-muted/30 flex items-center justify-between">
              <span className="text-xs font-semibold flex items-center gap-1.5">
                <CheckCircle size={13} weight="bold" /> Onboard Checklist
              </span>
              <span className={`text-xs font-bold font-mono ${checklist.ready ? "text-emerald-600 dark:text-emerald-400" : checklist.score >= 60 ? "text-amber-600 dark:text-amber-400" : "text-red-500"}`} data-testid="onboard-checklist-score">
                {checklist.done}/{checklist.total}
              </span>
            </div>
            <div className="px-4 py-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
              {checklist.items.map((i) => (
                <div key={i.key} className="flex items-center gap-2 text-xs" data-testid={`checklist-item-${i.key}`}>
                  {i.done
                    ? <CheckCircle size={14} weight="fill" className="text-emerald-500 shrink-0" />
                    : <XCircle size={14} weight="fill" className="text-red-400 shrink-0" />}
                  <span className={i.done ? "" : "text-muted-foreground"}>{i.label}</span>
                  {i.hint && <span className="text-[10px] text-muted-foreground truncate">· {i.hint}</span>}
                </div>
              ))}
            </div>
            {checklist.ready && !checklist.converted && (
              <div className="px-4 pb-3">
                <p className="text-[11px] text-emerald-600 dark:text-emerald-400 font-medium" data-testid="checklist-ready-note">
                  ✓ All checks passed — ready to onboard. Use "Approve &amp; Convert to Supplier" below.
                </p>
              </div>
            )}
          </div>
        )}

        {(app.documents || []).length > 0 && (
            <div className="border border-border rounded-lg overflow-hidden" data-testid="app-uploaded-docs">
              <div className="px-4 py-2.5 bg-muted/30 flex items-center gap-2">
                <Paperclip size={13} />
                <span className="text-xs font-semibold">Uploaded Documents ({app.documents.length})</span>
              </div>
              <div className="px-4 py-3 space-y-2">
                {app.documents.map((d, i) => (
                  <div key={d.file_id || i} className="flex flex-wrap items-center gap-2 text-xs" data-testid={`app-detail-doc-${i}`}>
                    <FileText size={14} className="text-muted-foreground shrink-0" />
                    <span className="font-medium truncate max-w-[180px]" title={d.file_name}>{d.file_name}</span>
                    <Badge variant="outline" className="text-[9px]">{(d.doc_type || "other").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</Badge>
                    {d.expiry_date && <span className="text-[10px] text-muted-foreground">exp {new Date(d.expiry_date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>}
                    <Button variant="outline" size="sm" className="h-6 text-[10px] ml-auto" onClick={() => viewDoc(d)} data-testid={`app-doc-view-${i}`}>View</Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Admin Notes */}
          <div>
            <label className="text-xs font-semibold mb-1 block">Admin Notes</label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} className="text-xs h-16"
              placeholder="Add internal notes about this application..." data-testid="admin-notes-input" />
          </div>

          {/* Review Info */}
          {app.reviewed_by && (
            <p className="text-[10px] text-muted-foreground">
              Reviewed by {app.reviewed_by} on {app.reviewed_at ? new Date(app.reviewed_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "N/A"}
            </p>
          )}

          {/* Approval steps (new applications only) */}
          {!app.converted_vendor_id && !app.linked_vendor_id && app.status !== "rejected" && (
            <div className="border border-emerald-200 dark:border-emerald-900 rounded-lg overflow-hidden" data-testid="approval-steps">
              <div className="px-4 py-2.5 bg-emerald-50 dark:bg-emerald-900/20">
                <span className="text-xs font-semibold text-emerald-800 dark:text-emerald-300">Approval Steps</span>
              </div>
              <div className="p-4 space-y-3">
                <label className="flex items-center gap-2.5 text-sm cursor-pointer">
                  <Checkbox
                    checked={app.status === "under_review" || app.status === "approved"}
                    disabled={loading || app.status === "approved"}
                    onCheckedChange={(c) => onStatusChange(app.id, c ? "under_review" : "pending", notes)}
                    data-testid="step-reviewed-checkbox"
                  />
                  <span className={app.status !== "pending" ? "" : "text-muted-foreground"}>Application reviewed</span>
                </label>
                <label className="flex items-center gap-2.5 text-sm cursor-pointer">
                  <Checkbox
                    checked={app.status === "approved"}
                    disabled={loading || app.status === "pending"}
                    onCheckedChange={(c) => onStatusChange(app.id, c ? "approved" : "under_review", notes)}
                    data-testid="step-approved-checkbox"
                  />
                  <span className={app.status === "approved" ? "" : "text-muted-foreground"}>Details verified &amp; approved</span>
                </label>
                <Button
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                  disabled={loading || app.status !== "approved"}
                  onClick={() => onOnboard(app.id)}
                  data-testid="add-supplier-to-portal-btn"
                >
                  <UserPlus size={15} className="mr-1.5" /> {loading ? "Working..." : "Add Supplier to Portal"}
                </Button>
                <p className="text-[10px] text-muted-foreground text-center">Creates the supplier record, carries their documents across, and emails their portal invitation — all in one go</p>
              </div>
            </div>
          )}

          {/* Merge change preview (profile updates only) */}
          {mergePreview && (
            <div className="border border-purple-200 dark:border-purple-900 rounded-lg overflow-hidden" data-testid="merge-preview">
              <div className="px-4 py-2.5 bg-purple-50 dark:bg-purple-900/20 flex items-center gap-2">
                <span className="text-xs font-semibold text-purple-700 dark:text-purple-300">
                  What will change on merge ({mergePreview.changes.length})
                </span>
                {mergePreview.documents_incoming > 0 && (
                  <span className="text-[10px] text-purple-600 dark:text-purple-400 ml-auto">+ {mergePreview.documents_incoming} document{mergePreview.documents_incoming === 1 ? "" : "s"}</span>
                )}
              </div>
              {mergePreview.changes.length === 0 ? (
                <p className="px-4 py-3 text-xs text-muted-foreground" data-testid="merge-preview-nochange">Submitted details match the current record — nothing will change.</p>
              ) : (
                <div className="divide-y divide-border/60">
                  <div className="grid grid-cols-[110px_1fr_1fr] gap-2 px-4 py-1.5 text-[9px] uppercase tracking-wider text-muted-foreground font-semibold">
                    <span>Field</span><span>Current</span><span>New</span>
                  </div>
                  {mergePreview.changes.map(c => (
                    <div key={c.field} className="grid grid-cols-[110px_1fr_1fr] gap-2 px-4 py-2 text-xs" data-testid={`merge-change-${c.field}`}>
                      <span className="font-semibold text-muted-foreground">{c.label}</span>
                      <span className="text-muted-foreground break-words">{c.current || <em className="opacity-60">empty</em>}</span>
                      <span className="text-emerald-700 dark:text-emerald-400 font-medium break-words">{c.incoming || <em className="opacity-60">empty</em>}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Converted notice */}
          {app.converted_vendor_id && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
              <CheckCircle size={16} className="text-emerald-600" weight="fill" />
              <span className="text-xs text-emerald-700 dark:text-emerald-300">{app.linked_vendor_id ? "Merged into supplier profile" : "Converted to supplier"}</span>
              <Button size="sm" variant="ghost" className="text-xs ml-auto h-7"
                onClick={() => { onClose(); navigate(`/vendors/${app.converted_vendor_id}`); }}
                data-testid="view-converted-supplier-btn"
              >
                View Supplier <ArrowRight size={12} className="ml-1" />
              </Button>
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2 mt-4">
          {/* Reject stays available until converted */}
          {!app.converted_vendor_id && !app.linked_vendor_id && app.status !== "rejected" && (
            <Button size="sm" variant="destructive" onClick={() => onStatusChange(app.id, "rejected", notes)}
              disabled={loading} data-testid="reject-application-btn">
              <XCircle size={14} className="mr-1" /> Reject
            </Button>
          )}
          {app.status === "rejected" && !app.converted_vendor_id && (
            <Button size="sm" variant="outline" onClick={() => onStatusChange(app.id, "pending", notes)}
              disabled={loading} data-testid="reopen-application-btn">
              <ArrowsClockwise size={14} className="mr-1" /> Reopen
            </Button>
          )}
          {app.linked_vendor_id && !app.converted_vendor_id && (
            <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white" onClick={() => onMerge(app.id)}
              disabled={loading} data-testid="merge-profile-btn">
              <CheckCircle size={14} className="mr-1" /> Merge into Profile
            </Button>
          )}
          {/* Save notes for any status */}
          {notes !== (app.admin_notes || "") && (
            <Button size="sm" variant="outline" onClick={() => onStatusChange(app.id, app.status, notes)}
              disabled={loading} data-testid="save-notes-btn">
              Save Notes
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ────────── Reminders Tab (original content) ────────── */
function RemindersTab() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [activation, setActivation] = useState(null);
  const [chaserCfg, setChaserCfg] = useState(null);
  const [cfgDays, setCfgDays] = useState("");
  const [cfgMaxAge, setCfgMaxAge] = useState("30");
  const [cfgEnabled, setCfgEnabled] = useState(true);
  const [savingCfg, setSavingCfg] = useState(false);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    Promise.all([
      API.get("/scheduler/onboarding-status").then(r => setData(r.data)).catch(() => {}),
      API.get("/scheduler/status").then(r => setSchedulerStatus(r.data)).catch(() => {}),
      API.get("/scheduler/activation-dashboard").then(r => setActivation(r.data)).catch(() => {}),
      API.get("/scheduler/chaser-settings").then(r => {
        setChaserCfg(r.data);
        setCfgDays((r.data.reminder_days || []).join(", "));
        setCfgMaxAge(String(r.data.max_age_days ?? 30));
        setCfgEnabled(r.data.enabled !== false);
      }).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const saveChaserCfg = async () => {
    const days = cfgDays.split(",").map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n));
    if (!days.length) { toast.error("Enter at least one reminder day, e.g. 3, 7, 14"); return; }
    setSavingCfg(true);
    try {
      const { data: saved } = await API.put("/scheduler/chaser-settings", {
        reminder_days: days, max_age_days: parseInt(cfgMaxAge, 10) || 30, enabled: cfgEnabled,
      });
      setChaserCfg(saved);
      setCfgDays((saved.reminder_days || []).join(", "));
      setCfgMaxAge(String(saved.max_age_days));
      toast.success(`Chaser schedule saved — reminders at ${saved.reminder_days.join(", ")} days`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    finally { setSavingCfg(false); }
  };

  const handleRunNow = async () => {
    setRunning(true);
    try {
      const { data: result } = await API.post("/scheduler/run-now");
      toast.success(result.message);
      const { data: refreshed } = await API.get("/scheduler/onboarding-status");
      setData(refreshed);
    } catch { toast.error("Failed to run"); }
    finally { setRunning(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;

  const cc = schedulerStatus?.compliance_check;

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button size="sm" onClick={handleRunNow} disabled={running} data-testid="run-now-btn">
          <ArrowsClockwise size={14} className={`mr-1 ${running ? "animate-spin" : ""}`} /> {running ? "Running..." : "Run Reminders Now"}
        </Button>
      </div>

      {/* Scheduler Health */}
      {cc && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="scheduler-stats">
          <StatCard label="Scheduler" value={cc.health === "healthy" ? "Healthy" : cc.health} icon={Heartbeat}
            color={cc.health === "healthy" ? "emerald" : "amber"} />
          <StatCard label="Total Runs" value={cc.total_runs} icon={Clock} />
          <StatCard label="Reminders Sent" value={cc.total_reminders} icon={EnvelopeSimple} color="blue" />
          <StatCard label="Pending Activations" value={data?.total_pending || 0} icon={Users}
            color={data?.total_pending > 0 ? "amber" : "emerald"} />
        </div>
      )}

      {/* Automated Reminder Info */}
      <div className="border border-border rounded-lg p-4 bg-card mb-6" data-testid="reminder-info">
        <h2 className="text-sm font-semibold font-[Chivo] mb-2">Automated Reminders</h2>
        <div className="grid gap-3 sm:grid-cols-3">
          <ReminderInfo title="Onboarding" desc="Reminders at 3, 7, and 14 days after portal invite is sent to suppliers who haven't activated" />
          <ReminderInfo title="Missing Documents" desc="Weekly email to suppliers missing required compliance documents (insurance, public liability, risk assessment, method statement)" />
          <ReminderInfo title="Expiring Documents" desc="Alerts at 30, 14, and 7 days before a compliance document expires, sent to suppliers and admins" />
        </div>
      </div>

      {/* Pending Onboarding */}
      <div className="mb-6" data-testid="pending-onboarding">
        <h2 className="text-sm font-semibold font-[Chivo] mb-3">Pending Portal Activations ({data?.total_pending || 0})</h2>
        {(!data?.pending_onboarding || data.pending_onboarding.length === 0) ? (
          <div className="text-center py-10 border border-dashed border-border rounded-lg">
            <CheckCircle size={28} className="mx-auto text-emerald-500 mb-2" />
            <p className="text-sm text-muted-foreground">All invited suppliers have activated their portals</p>
          </div>
        ) : (
          <div className="space-y-2">
            {data.pending_onboarding.map(s => (
              <div key={s.supplier_id}
                className="flex items-center justify-between p-3.5 border border-border rounded-lg bg-card hover:bg-muted/30 transition-colors cursor-pointer"
                onClick={() => navigate(`/vendors/${s.supplier_id}`)}
                data-testid={`onboarding-row-${s.supplier_id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-9 w-9 rounded-lg bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center shrink-0">
                    <Clock size={18} className="text-amber-600 dark:text-amber-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold truncate">{s.company_name}</p>
                    <p className="text-[10px] text-muted-foreground">{s.email} - {s.contact_name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={`text-[10px] ${s.days_since_invite > 7 ? "text-red-600 border-red-300" : "text-amber-600 border-amber-300"}`}>
                    {s.days_since_invite}d since invite
                  </Badge>
                  <Badge variant="outline" className="text-[10px]">
                    {s.reminders_sent} reminder{s.reminders_sent !== 1 ? "s" : ""} sent
                  </Badge>
                  <ArrowRight size={14} className="text-muted-foreground" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Chaser Settings — editable schedule */}
      {chaserCfg && (
        <div className="border border-border rounded-lg p-4 bg-card mb-6" data-testid="chaser-settings">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-sm font-semibold font-[Chivo]">Chaser Schedule</h2>
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <Checkbox checked={cfgEnabled} onCheckedChange={(v) => setCfgEnabled(!!v)} data-testid="chaser-enabled-toggle" />
              Enabled
            </label>
          </div>
          <p className="text-[11px] text-muted-foreground mb-3">Reminder emails go out this many days after an invite if the supplier hasn't activated. Change anytime — no code needed.</p>
          <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto] items-end">
            <div>
              <Label className="text-[10px]">Reminder days (comma separated)</Label>
              <Input value={cfgDays} onChange={(e) => setCfgDays(e.target.value)} placeholder="3, 7, 14" className="h-8 text-xs" data-testid="chaser-days-input" />
            </div>
            <div>
              <Label className="text-[10px]">Ignore invites older than (days)</Label>
              <Input type="number" value={cfgMaxAge} onChange={(e) => setCfgMaxAge(e.target.value)} className="h-8 text-xs w-32" data-testid="chaser-maxage-input" />
            </div>
            <Button size="sm" className="h-8 text-xs" onClick={saveChaserCfg} disabled={savingCfg} data-testid="chaser-save-btn">
              {savingCfg ? "Saving…" : "Save Schedule"}
            </Button>
          </div>
        </div>
      )}

      {/* Activation Dashboard — chaser effectiveness */}
      {activation && (
        <div className="mb-6" data-testid="activation-dashboard">
          <h2 className="text-sm font-semibold font-[Chivo] mb-3">Activation Dashboard <span className="text-muted-foreground font-normal text-xs">— is the chaser working?</span></h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <StatCard label="Suppliers Chased" value={activation.summary.total_chased} icon={EnvelopeSimple} />
            <StatCard label="Activated After Chase" value={activation.summary.activated} icon={CheckCircle} color="emerald" />
            <StatCard label="Still Pending" value={activation.summary.still_pending} icon={Clock} color="amber" />
            <StatCard label="Activation Rate" value={`${activation.summary.activation_rate}%`} icon={Heartbeat} color={activation.summary.activation_rate >= 50 ? "emerald" : "amber"} />
          </div>
          <div className="grid gap-3 sm:grid-cols-3 mb-4">
            {activation.stages.map(s => (
              <div key={s.stage} className="border border-border rounded-lg p-3 bg-card" data-testid={`activation-stage-${s.stage}`}>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{s.stage.replace("d", "")}-day reminder</p>
                <p className="text-xs">
                  <span className="font-bold font-mono">{s.sent}</span> sent · <span className="font-bold font-mono text-emerald-600 dark:text-emerald-400">{s.activated_after}</span> activated after
                </p>
                <div className="h-1.5 bg-muted rounded-full mt-2 overflow-hidden">
                  <div className="h-full bg-emerald-500 rounded-full transition-[width]" style={{ width: `${s.sent ? (s.activated_after / s.sent) * 100 : 0}%` }} />
                </div>
              </div>
            ))}
          </div>
          {activation.rows.length === 0 ? (
            <p className="text-xs text-muted-foreground border border-dashed border-border rounded-lg p-6 text-center" data-testid="activation-empty">
              No suppliers have been chased yet — results appear here once the first reminders go out.
            </p>
          ) : (
            <div className="space-y-2">
              {activation.rows.map(r => (
                <div key={r.vendor_id} className="flex flex-wrap items-center justify-between gap-2 p-3 border border-border rounded-lg bg-card" data-testid={`activation-row-${r.vendor_id}`}>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold truncate">{r.company_name}</p>
                    <p className="text-[10px] text-muted-foreground truncate">{r.email}</p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {r.reminders_sent.map(n => <Badge key={n} variant="outline" className="text-[9px]">{n} sent</Badge>)}
                    {r.activated ? (
                      <Badge className="text-[9px] bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                        Activated{r.activated_after ? ` after ${r.activated_after} reminder` : ""}{r.days_after_reminder != null ? ` (+${r.days_after_reminder}d)` : ""}
                      </Badge>
                    ) : (
                      <Badge className="text-[9px] bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">Still pending</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Recent Missing Doc Nudges */}
      {data?.recent_missing_doc_nudges?.length > 0 && (
        <div data-testid="missing-doc-nudges">
          <h2 className="text-sm font-semibold font-[Chivo] mb-3">Recent Missing Document Reminders</h2>
          <div className="border border-border rounded-lg overflow-hidden bg-card">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="text-left px-4 py-2.5 font-semibold">Supplier</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Missing Documents</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Sent</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.recent_missing_doc_nudges.map((n, i) => (
                  <tr key={i} className="hover:bg-muted/30">
                    <td className="px-4 py-2.5">{n.company_name || n.vendor_id?.slice(0, 12)}</td>
                    <td className="px-4 py-2.5">
                      {(n.missing || []).map(d => (
                        <Badge key={d} variant="outline" className="text-[9px] mr-1 text-red-600 border-red-300">
                          {d.replace(/_/g, " ")}
                        </Badge>
                      ))}
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground">{n.sent_at ? new Date(n.sent_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color = "primary" }) {
  const colors = {
    primary: "text-primary",
    emerald: "text-emerald-600 dark:text-emerald-400",
    amber: "text-amber-600 dark:text-amber-400",
    blue: "text-blue-600 dark:text-blue-400",
  };
  return (
    <div className="border border-border rounded-lg p-4 bg-card" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <Icon size={18} className={`${colors[color]} mb-2`} weight="duotone" />
      <p className="text-lg font-bold font-[Chivo]">{value}</p>
      <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">{label}</p>
    </div>
  );
}

function ReminderInfo({ title, desc }) {
  return (
    <div className="p-3 rounded-lg bg-muted/30">
      <p className="text-xs font-semibold mb-1">{title}</p>
      <p className="text-[10px] text-muted-foreground leading-relaxed">{desc}</p>
    </div>
  );
}
